import Vue from 'vue';
import axios from 'axios';
import upperFirst from 'lodash/upperFirst';
import merge from 'lodash/merge';
import { loadEntity, loadEntityResponseData } from '@/utilities/normalization';
import { subscribe, unsubscribe } from '@/echo/broadcasts';
import trim from 'lodash/trim';

// Example options:
// {
//     single: 'orderItem',
//     multiple: 'orderItems',
//     endpoint: 'order-items',
//     singular: 'OrderItem',
//     plural: 'OrderItems',
// }

function normalizeOptions(options) {
    if (typeof options === 'string')
        options = { single: options };

    if (options.normalized)
        return options;

    if (!options.multiple)
        options.multiple = options.single + 's';

    let defaults = {
        endpoint: options.multiple.toLowerCase(),
        singular: upperFirst(options.single),
        plural: upperFirst(options.multiple),
        eventChannel: options.multiple,
        eventNamespace: '',
    };

    options = merge({}, defaults, options);

    let eventNamespace = trim(options.eventNamespace, '.');
    if (eventNamespace)
        eventNamespace += '.';

    defaults = {
        actions: {
            index: `get${options.plural}`,
            show: `get${options.singular}`,
            store: `create${options.singular}`,
            update: `update${options.singular}`,
            delete: `delete${options.singular}`,
            subscribe: `subscribe${options.plural}`,
            subscribeSingle: `subscribeSingle${options.singular}`,
            unsubscribe: `unsubscribe${options.plural}`,
            unsubscribeSingle: `unsubscribeSingle${options.singular}`,
        },
        broadcastEvents: {
            created: `${eventNamespace}${options.singular}Created`,
            updated: `${eventNamespace}${options.singular}Updated`,
        },
    };

    options = merge({ normalized: true }, defaults, options);

    return options;
}

function request(options, config = null) {
    options = normalizeOptions(options);

    config = Object.assign({
        method: 'get',
        url: `/api/${options.endpoint}`,
    }, config);

    if (config.data instanceof FormData && config.method !== 'post') {
        config.data.append('_method', config.method);
        config.method = 'post';
    }

    return axios(config);
}

export function getId(data) {
    if (data instanceof FormData)
        return data.get('id');
    return data.id;
}

export async function multipleEntityRequest(options, config = null) {
    options = normalizeOptions(options);

    let response = await request(options, config);
    return loadEntityResponseData(options.multiple, response.data);
}

export async function singleEntityRequest(options, config = null) {
    options = normalizeOptions(options);

    let response = await request(options, config);
    return loadEntityResponseData(options.single, response.data);
}

function enhanceOrAdd(collection, key, object) {
    if (collection[key])
        object = Object.assign({}, collection[key], object);

    Vue.set(collection, key, object);
}

export function generateVuexCrud(options) {
    options = normalizeOptions(options);

    let state = {
        byId: {},
        meta: options,
    };

    let broadcastCallback = (event) => {
        loadEntity(options.single, event[options.single]);
    };

    let actions = {
        // General CRUD
        [options.actions.index](store, params = {}) {
            return multipleEntityRequest(options, { params });
        },
        [options.actions.show](store, { id }) {
            return singleEntityRequest(options, {
                url: `/api/${options.endpoint}/${id}`,
            });
        },
        [options.actions.store](store, payload) {
            let entity = payload[options.single];
            let onUploadProgress = payload.onUploadProgress;
            return singleEntityRequest(options, {
                method: 'post',
                data: entity,
                onUploadProgress,
            });
        },
        [options.actions.update](store, payload) {
            let entity = payload[options.single];
            let id = getId(entity);
            return singleEntityRequest(options, {
                method: 'patch',
                url: `/api/${options.endpoint}/${id}`,
                data: entity,
            });
        },
        async [options.actions.delete]({ commit }, payload) {
            let entity = payload[options.single];
            let response = await axios.delete(`/api/${options.endpoint}/${entity.id}`);
            // entity = response.data.data;
            entity = response.data;
            return commit('DELETE_ENTITY', { [options.single]: entity });
        },

        // Broadcast subscriptions
        [options.actions.subscribe]() {
            subscribe(options.eventChannel, options.broadcastEvents.created, broadcastCallback);
            subscribe(options.eventChannel, options.broadcastEvents.updated, broadcastCallback);
        },
        [options.actions.unsubscribe]() {
            unsubscribe(options.eventChannel, options.broadcastEvents.created, broadcastCallback);
            unsubscribe(options.eventChannel, options.broadcastEvents.updated, broadcastCallback);
        },
        [options.actions.subscribeSingle](store, { id }) {
            subscribe(`${options.eventChannel}.${id}`, options.broadcastEvents.updated, broadcastCallback);
        },
        [options.actions.unsubscribeSingle](store, { id }) {
            unsubscribe(`${options.eventChannel}.${id}`, options.broadcastEvents.updated, broadcastCallback);
        },
    };

    let mutations = {
        LOAD_ENTITIES(state, payload) {
            let entities = payload[options.multiple];
            if (entities)
                Object.values(entities).forEach(entity => enhanceOrAdd(state.byId, entity.id, entity));
        },
        DELETE_ENTITY(state, payload) {
            let entity = payload[options.single];
            if (entity)
                Vue.delete(state.byId, entity.id);
        },
    };

    return {
        state,
        actions,
        mutations,
    };
}
